import React from 'react'
import {
  LayersPanelContainer,
  LayersHeader,
  LayersTitle,
  LayersClose,
  LayersContent,
  SearchContainer,
  SearchInputWrapper,
  SearchInput,
  SearchIcon,
  CollapsibleGroupHeader,
  ChevronIcon,
  GroupContent,
  LayerGroupCard,
  LayerGroupHeaderLeft,
  LayerGroupIcon,
  LayerGroupTitleWrapper,
  LayerGroupTitle,
  SectionBadge,
} from './LayersPanel.styles'
import { LayersPanelProps } from './LayersPanel.types'
import { Icon } from '../../atoms/Icon'
import {
  LayersPanelExclusiveDropdownGroup,
  LayersPanelMultipleCheckboxGroup,
} from './components'

export const LayersPanel: React.FC<LayersPanelProps> = ({
  active,
  onClose,
  layerGroups,
  onLayerToggle,
  onLayerSelect,
  onLayerOpacityChange,
  title = 'Map Layers',
  className,
  position,
  width,
  zIndex,
  enableSearch = true,
  searchPlaceholder = 'Search layers...',
  renderCustomControls,
}) => {
  const [searchQuery, setSearchQuery] = React.useState('')
  const [expandedGroups, setExpandedGroups] = React.useState<
    Record<string, boolean>
  >(() => {
    // Initialize expanded state based on defaultExpanded
    const initial: Record<string, boolean> = {}
    layerGroups.forEach((group) => {
      initial[group.id] = group.defaultExpanded ?? true
    })
    return initial
  })

  const handleLayerToggle = (layerId: string, enabled: boolean) => {
    onLayerToggle?.(layerId, enabled)
  }

  const handleLayerSelect = (layerId: string, value: string) => {
    onLayerSelect?.(layerId, value)
  }

  const handleOpacityChange = (layerId: string, opacity: number) => {
    onLayerOpacityChange?.(layerId, opacity)
  }

  // Quick actions for showing/hiding all layers in a group
  const handleShowAll = (groupId: string) => {
    const group = layerGroups.find((g) => g.id === groupId)
    if (!group) return

    group.layers.forEach((layer) => {
      if (layer.type !== 'radio' && !layer.disabled) {
        handleLayerToggle(layer.id, true)
      }
    })
  }

  const handleHideAll = (groupId: string) => {
    const group = layerGroups.find((g) => g.id === groupId)
    if (!group) return

    group.layers.forEach((layer) => {
      if (layer.type !== 'radio' && !layer.disabled) {
        handleLayerToggle(layer.id, false)
      }
    })
  }

  // Get icon based on group - use custom icon if provided, otherwise fallback to type-based icon
  const getGroupIcon = (groupIcon?: string, groupType?: 'base' | 'dynamic') => {
    if (groupIcon) return groupIcon
    if (groupType === 'base') return 'map'
    if (groupType === 'dynamic') return 'layers'
    return null
  }

  // Check if a layer is a demographics layer (for legend rendering)
  const isDemographicsLayer = (layerId: string) => {
    return layerId.startsWith('demographics-')
  }

  // Check if a layer is an employment layer (for legend rendering)
  const isEmploymentLayer = (layerId: string) => {
    return layerId.startsWith('employment-')
  }

  // Check if a layer is a pricing layer (for legend rendering)
  const isPricingLayer = (layerId: string) => {
    return (
      layerId.startsWith('purchase-market-') ||
      layerId.startsWith('rental-market-')
    )
  }

  // Check if a layer is a noise layer (for legend rendering)
  const isNoiseLayer = (layerId: string) => {
    return layerId.startsWith('noise-')
  }

  // Check if a layer is a sunlight layer (for legend rendering)
  const isSunlightLayer = (layerId: string) => {
    return layerId === 'sunlight-hours'
  }

  // Check if a layer is a sun direction layer (for legend rendering)
  const isSunDirectionLayer = (layerId: string) => {
    return layerId === 'sun-direction'
  }

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }))
  }

  // Filter layers based on search query
  const filteredGroups = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return layerGroups
    }

    const query = searchQuery.toLowerCase()
    return layerGroups
      .map((group) => ({
        ...group,
        layers: group.layers.filter((layer) =>
          layer.label.toLowerCase().includes(query)
        ),
      }))
      .filter((group) => group.layers.length > 0)
  }, [layerGroups, searchQuery])

  // Auto-expand groups when searching
  React.useEffect(() => {
    if (searchQuery.trim()) {
      // Expand all groups when searching
      const allExpanded: Record<string, boolean> = {}
      filteredGroups.forEach((group) => {
        allExpanded[group.id] = true
      })
      setExpandedGroups(allExpanded)
    }
  }, [searchQuery, filteredGroups])

  return (
    <LayersPanelContainer
      $active={active}
      $position={position}
      $width={width}
      $zIndex={zIndex}
      className={className}
    >
      <LayersHeader>
        <LayersTitle>{title}</LayersTitle>
        <LayersClose onClick={onClose} aria-label="Close layers panel">
          <Icon name="x" size="small" />
        </LayersClose>
      </LayersHeader>

      {enableSearch && (
        <SearchContainer>
          <SearchInputWrapper>
            <SearchIcon>
              <Icon name="search" size="small" />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search layers"
            />
          </SearchInputWrapper>
        </SearchContainer>
      )}

      <LayersContent>
        {filteredGroups.map((group) => {
          const isExpanded = expandedGroups[group.id] ?? true
          const groupIcon = getGroupIcon(group.icon, group.groupType)

          // Determine render type - default to 'multiple-checkbox' if not specified
          const renderType = group.groupRenderType || 'multiple-checkbox'

          // For exclusive-dropdown, expect a single layer with options
          const exclusiveLayer =
            renderType === 'exclusive-dropdown' ? group.layers[0] : null

          return (
            <LayerGroupCard key={group.id} $groupType={group.groupType}>
              {/* Collapsible Group Header */}
              <CollapsibleGroupHeader
                $expanded={isExpanded}
                onClick={() => toggleGroup(group.id)}
                role="button"
                aria-expanded={isExpanded}
                aria-controls={`group-content-${group.id}`}
              >
                <LayerGroupHeaderLeft>
                  {groupIcon && (
                    <LayerGroupIcon>
                      <Icon name={groupIcon as any} size="small" />
                    </LayerGroupIcon>
                  )}
                  <LayerGroupTitleWrapper>
                    {group.groupType === 'base' ? (
                      <LayerGroupTitle>{group.title}</LayerGroupTitle>
                    ) : (
                      <SectionBadge>{group.title}</SectionBadge>
                    )}
                  </LayerGroupTitleWrapper>
                </LayerGroupHeaderLeft>
                <ChevronIcon $expanded={isExpanded}>
                  <Icon name="arrow-down" size="small" />
                </ChevronIcon>
              </CollapsibleGroupHeader>

              {/* Group Content */}
              <GroupContent
                $expanded={isExpanded}
                id={`group-content-${group.id}`}
              >
                {renderType === 'exclusive-dropdown' && exclusiveLayer ? (
                  <LayersPanelExclusiveDropdownGroup
                    layer={exclusiveLayer}
                    isExpanded={isExpanded}
                    onLayerSelect={handleLayerSelect}
                    onOpacityChange={handleOpacityChange}
                    isDemographicsLayer={isDemographicsLayer}
                    isEmploymentLayer={isEmploymentLayer}
                    isPricingLayer={isPricingLayer}
                    isNoiseLayer={isNoiseLayer}
                    isSunlightLayer={isSunlightLayer}
                    isSunDirectionLayer={isSunDirectionLayer}
                    showNoLayerOption={group.showNoLayerOption}
                    renderCustomControls={renderCustomControls}
                  />
                ) : (
                  <LayersPanelMultipleCheckboxGroup
                    group={group}
                    isExpanded={isExpanded}
                    onLayerToggle={handleLayerToggle}
                    onShowAll={() => handleShowAll(group.id)}
                    onHideAll={() => handleHideAll(group.id)}
                    onOpacityChange={handleOpacityChange}
                    isDemographicsLayer={isDemographicsLayer}
                    isEmploymentLayer={isEmploymentLayer}
                    isPricingLayer={isPricingLayer}
                    isNoiseLayer={isNoiseLayer}
                    isSunDirectionLayer={isSunDirectionLayer}
                  />
                )}
              </GroupContent>
            </LayerGroupCard>
          )
        })}
      </LayersContent>
    </LayersPanelContainer>
  )
}
